// src/services/authService.js
import { supabase } from '../config';
import { jwtDecode } from 'jwt-decode';

/**
 * Enhanced authentication service with proper JWT validation
 */

// JWT validation configuration - Made less strict for Supabase compatibility
const JWT_CONFIG = {
  algorithms: ['HS256'],
  // Remove strict issuer/audience checks for Supabase compatibility
  issuer: null, // Allow any issuer
  audience: null // Allow any audience
};

/**
 * Validates JWT token with proper checks
 * @param {string} token - JWT token to validate
 * @returns {Object|null} - Decoded token or null if invalid
 */
export const validateJWT = (token) => {
  try {
    if (!token) return null;
    
    // Basic token structure validation
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode without verification first to check structure
    const decoded = jwtDecode(token);
    if (!decoded) return null;
    
    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp < now) {
      console.warn('Token expired');
      return null;
    }
    
    // Check issuer and audience if configured (skip for Supabase compatibility)
    if (JWT_CONFIG.issuer && decoded.iss !== JWT_CONFIG.issuer) {
      console.warn('Invalid token issuer');
      return null;
    }
    
    if (JWT_CONFIG.audience && decoded.aud !== JWT_CONFIG.audience) {
      console.warn('Invalid token audience');
      return null;
    }
    
    return decoded;
  } catch (error) {
    console.error('JWT validation error:', error);
    return null;
  }
};

/**
 * Enhanced token verification for API endpoints
 * @param {string} authHeader - Authorization header
 * @returns {Object|null} - User info or null if invalid
 */
export const verifyAPIToken = async (authHeader) => {
  try {
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }
    
    const token = authHeader.substring(7);
    const decoded = validateJWT(token);
    
    if (!decoded) return null;
    
    // Verify token with Supabase
    const { data: { user }, error } = await supabase.auth.getUser(token);
    
    if (error || !user) {
      console.error('Supabase token verification failed:', error);
      return null;
    }
    
    return {
      id: user.id,
      email: user.email,
      ...decoded
    };
  } catch (error) {
    console.error('API token verification error:', error);
    return null;
  }
};

/**
 * Rate limiting configuration
 */
const RATE_LIMITS = {
  api: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 100, // limit each IP to 100 requests per windowMs
    skipSuccessfulRequests: true
  },
  auth: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 5, // limit each IP to 5 auth requests per windowMs
    skipSuccessfulRequests: false
  }
};

/**
 * Simple in-memory rate limiter (for development)
 * In production, use Redis or similar
 */
const rateLimitStore = new Map();

export const checkRateLimit = (ip, type = 'api') => {
  const config = RATE_LIMITS[type] || RATE_LIMITS.api;
  const key = `${ip}:${type}`;
  const now = Date.now();
  
  if (!rateLimitStore.has(key)) {
    rateLimitStore.set(key, { count: 1, resetTime: now + config.windowMs });
    return true;
  }
  
  const record = rateLimitStore.get(key);
  
  if (now > record.resetTime) {
    rateLimitStore.set(key, { count: 1, resetTime: now + config.windowMs });
    return true;
  }
  
  if (record.count >= config.max) {
    return false;
  }
  
  record.count++;
  return true;
};

/**
 * Input validation utilities
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validatePassword = (password) => {
  // Minimum 8 characters, at least one letter and one number
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
  return passwordRegex.test(password);
};

export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove potentially dangerous characters
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

/**
 * Enhanced session management
 */
export const getCurrentSession = async () => {
  try {
    console.log('getCurrentSession: Starting...');
    
    // Add timeout to prevent hanging
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('getCurrentSession timeout')), 1500)
    );
    
    const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]);
    console.log('getCurrentSession: Result:', { session: !!session, error });
    
    if (error) {
      console.error('Session retrieval error:', error);
      return null;
    }
    
    // Validate session token
    if (session?.access_token) {
      const isValid = validateJWT(session.access_token);
      if (!isValid) {
        console.log('Token invalid, attempting refresh...');
        // Token is invalid, try to refresh
        const refreshPromise = supabase.auth.refreshSession();
        const refreshTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('refresh timeout')), 1000)
        );
        
        try {
          const { data: { session: refreshedSession }, error: refreshError } = 
            await Promise.race([refreshPromise, refreshTimeoutPromise]);
          
          if (refreshError || !refreshedSession) {
            console.error('Session refresh failed:', refreshError);
            await supabase.auth.signOut();
            return null;
          }
          
          return refreshedSession;
        } catch (refreshError) {
          console.error('Session refresh error:', refreshError);
          return null;
        }
      }
    }
    
    return session;
  } catch (error) {
    console.error('Session management error:', error);
    return null;
  }
};

export const getCurrentUser = async () => {
  try {
    console.log('getCurrentUser: Starting...');
    
    // Simple session check with timeout
    const sessionPromise = getCurrentSession();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('getCurrentUser timeout')), 2000)
    );
    
    const session = await Promise.race([sessionPromise, timeoutPromise]);
    console.log('getCurrentUser: Session result:', session);
    
    return session?.user || null;
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};

export const isAuthenticated = async () => {
  try {
    const user = await getCurrentUser();
    return !!user;
  } catch (error) {
    console.error('Authentication check error:', error);
    return false;
  }
};

/**
 * Gets auth headers for API requests with enhanced security
 * @returns {Promise<Object>} Headers object with authorization
 */
export const getAuthHeaders = async () => {
  try {
    const session = await getCurrentSession();
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    return headers;
  } catch (error) {
    console.error('Get auth headers error:', error);
    return {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
  }
};

/**
 * Waits for authentication to be ready
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Object|null>} The user object or null
 */
export const waitForAuth = async (timeout = 5000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    const user = await getCurrentUser();
    if (user) return user;
    
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return null;
};

/**
 * Secure logout with cleanup
 */
export const secureLogout = async () => {
  try {
    // Clear rate limit store for this session
    rateLimitStore.clear();
    
    // Sign out from Supabase
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Logout error:', error);
    }
    
    // Clear any local storage
    localStorage.clear();
    sessionStorage.clear();
    
    return !error;
  } catch (error) {
    console.error('Secure logout error:', error);
    return false;
  }
};

/**
 * Fetch user profile from database
 * @param {string} userId - User ID
 * @param {Object} profileData - Optional profile data to update
 * @returns {Promise<Object>} User profile
 */
export const fetchUserProfile = async (userId, profileData = null) => {
  try {
    if (profileData) {
      // Update profile
      const { data, error } = await supabase
        .from('user_profiles')
        .update(profileData)
        .eq('id', userId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      // Fetch profile
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }
      return data;
    }
  } catch (error) {
    console.error('Fetch user profile error:', error);
    return null;
  }
};

/**
 * Fetch user roles from database
 * @param {string} userId - User ID
 * @returns {Promise<Array>} User roles
 */
export const fetchUserRoles = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    if (error) throw error;
    return data.map(item => item.role);
  } catch (error) {
    console.error('Fetch user roles error:', error);
    return [];
  }
};

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Updated profile
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(profileData)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Update user profile error:', error);
    throw error;
  }
};

/**
 * Sign out user
 * @returns {Promise<boolean>} Success status
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Sign out error:', error);
    return false;
  }
};

// Legacy exports for backward compatibility
const authService = {
  getCurrentUser,
  getCurrentSession,
  isAuthenticated,
  getAuthHeaders,
  waitForAuth,
  secureLogout,
  validateEmail,
  validatePassword,
  sanitizeInput,
  validateJWT,
  verifyAPIToken,
  checkRateLimit,
  fetchUserProfile,
  fetchUserRoles,
  updateUserProfile,
  signOut
};

export default authService;